import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { getTokenLimit } from '../repository/get-token-limit'
import { SelectedFilesActions } from './slice'
import { defaultMaxTokenResponse, getFileEmbeddingsCount } from '@/routes/dashboard/files/utils/files-utils'
import { AppDispatch, RootState } from '@/store/store'
import { setFileProcessingStatus, FileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'

export const selectedFilesListenerMiddleware = createListenerMiddleware()
const startListener = selectedFilesListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

/**
 * File Processing Status Changes Listener
 *
 * SINGLE PURPOSE: Respond to file processing status changes.
 *
 * - When file starts processing (PENDING/PROCESSING): Clear cached embeddings
 * - When file completes processing (SUCCESS): Trigger embeddings fetch
 */
startListener({
  actionCreator: setFileProcessingStatus,
  effect: async (action, listenerApi) => {
    const { sourcePath, status } = action.payload

    // Clear cache when file starts (re-)processing
    if (status === FileProcessingStatus.PROCESSING || status === FileProcessingStatus.PENDING) {
      listenerApi.dispatch(SelectedFilesActions.clearFileEmbeddingsCache({ filePath: sourcePath }))
      return
    }

    // Trigger cache update when file processing completes successfully
    if (status === FileProcessingStatus.SUCCESS) {
      listenerApi.dispatch(SelectedFilesActions.updateEmbeddingsCache({ filePath: sourcePath }))
    }
  },
})

/**
 * File Selection Changes Listener
 *
 * SINGLE PURPOSE: Respond to file addition actions.
 *
 * When a file is added to a conversation, trigger embeddings fetch for that file.
 * Note: removeFile and clearFiles are NOT handled - cache persists for performance.
 */
startListener({
  matcher: isAnyOf(AgentConversationFormActions.addFile, AgentConversationFormActions.addTemplateFile),
  effect: (action, listenerApi) => {
    const { filePath } = action.payload as { filePath: string }

    // Trigger embeddings cache update for this file
    listenerApi.dispatch(SelectedFilesActions.updateEmbeddingsCache({ filePath }))
  },
})

/**
 * Embeddings Cache Management Listener
 *
 * 1. Fetches token limit from backend (if needed),
 *
 * 2. Fetches embeddings count for the specified file
 *    and stores it in fileEmbeddingsCache.
 */
startListener({
  actionCreator: SelectedFilesActions.updateEmbeddingsCache,
  effect: async (action, listenerApi) => {
    const { filePath } = action.payload

    // Get the current state
    const state = listenerApi.getState()

    // Token limit state
    const tokenLimitLoading = state.selectedFilesState.tokenLimit.loading
    const tokenLimitResponse = state.selectedFilesState.tokenLimit.response

    // Update the token limit from backend (only once per session)
    // Skip if it's loading or if it's not the default
    try {
      // Get the token limit if it's not loading and if it's still the default
      if (!tokenLimitLoading && tokenLimitResponse === defaultMaxTokenResponse) {
        // Dispatch loading status
        listenerApi.dispatch(SelectedFilesActions.setTokenLimitLoading(true))

        // Get the token limit
        const response = await getTokenLimit()

        // Dispatch the state update
        listenerApi.dispatch(SelectedFilesActions.setTokenLimitResponse(response))
      }
    } catch (e) {
      // Do nothing (sentry error captured in fetch function)
    } finally {
      // Dispatch the loading status
      listenerApi.dispatch(SelectedFilesActions.setTokenLimitLoading(false))
    }

    // Check if already cached
    const cache = state.selectedFilesState.fileEmbeddingsCache
    const cached = cache[filePath]
    if (cached !== undefined) {
      // Already cached - no need to fetch
      return
    }

    // Fetch embeddings count from GCS
    const count = await getFileEmbeddingsCount(filePath)

    // Cache the result (including 0) to avoid redundant GCS fetches
    listenerApi.dispatch(SelectedFilesActions.setFileEmbeddingsCount({ filePath, count }))
  },
})
